import { useCallback, useMemo, useState } from 'react'
import styled, { useTheme } from 'styled-components'

import {
  SupplyPool,
  ClmmPool,
  PositionConfigInfo,
  PhantomTypeArgument,
  TypeArgument,
  StructClass,
  CoinInfo,
  SUI,
  USDC,
  Price,
} from '@kunalabs-io/kai'
import { CetusRedemptionUtil } from '@kunalabs-io/kai'

import { ReductionInfoSubmit } from '../types'
import { PositionInfo } from '@/shared/kai/hooks/useUserPositions'
import { useRewardsFeesInfo } from '../hooks/useRewardFeeInfos'
import { useRewardsFeesTotalValue } from '../hooks/useRewardsFeesTotalValue'
import Checkbox from 'components/Checkbox/Checkbox'
import { ConvertRewardsToMenu } from '../ConvertRewardsSelect'
import { vaultDisplayInfoList } from 'utils/vaultDisplayInfo'
import { ExpansionPanel } from 'components/ExpansionPanel/ExpansionPanel'
import { WithdrawSummary } from './components/WithdrawSummary'
import { Equity } from './components/Equity'
import { WithdrawDetails } from './components/WithdrawDetails'
import { PendingFeesRewardsNew } from './components/PendingFeesRewardsNew'
import { SwapRouter, useRouter } from 'contexts/SwapRouterContext'
import { AmountSlider, MAX_SLIDER, MIN_SLIDER } from './components/AmountSlider'
import { DialogActions } from './components/DialogActions'
import { useDebouncedValueWithLoading } from '../hooks/useDebounce'
import { Spinner } from 'components/Spinner/Spinner'
import { useWithdrawDetails } from './hooks/useWithdrawDetails'
import { useDryRunData } from './hooks/useDryRunData'
import { useReductionInfo } from './hooks/useReductionInfo'
import { WithdrawError } from './components/WithdrawError'
import { getMaxHeightForAdditionalContent, isSwapAvailable } from './utils'
import { Banner } from 'components/Banner/Banner'
import { RepayDebtAndCetusRedemption } from './components/RepayDebtAndCetusRedemption'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { IconInfo } from 'components/Icons/InfoIcon'
import { Dialog } from 'components/Dialog/Dialog'
import { Typography } from 'components/Typography/Typography'
import { useIsMobile } from 'utils/useIsMobile'
import { useTokenPrice } from 'utils/useTokenPrice'

const CheckboxContainer = styled.div`
  display: inline-flex;
  margin-top: 24px;
  margin-bottom: 16px;
  cursor: pointer;
`

const ExpansionPanelTitleContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const AdditionalContent = styled.div<{ $visible: boolean; $pendingRewardsNum: number }>`
  max-height: ${({ $visible, $pendingRewardsNum }) =>
    $visible ? `${getMaxHeightForAdditionalContent($pendingRewardsNum)}px` : '0'};
  opacity: ${({ $visible }) => ($visible ? '1' : '0')};
  overflow: hidden;
  width: 50%;
  transition:
    max-height 0.5s ease,
    opacity 0.5s ease;

  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    // max-height: ${({ $visible }) => ($visible ? '440px' : '0')};
    width: 100%;
  }
`

const PositionBreakdownTitle = styled(Typography)`
  margin: 16px 0;
  display: flex;
  align-items: center;
  & > div {
    display: flex;
    cursor: pointer;
    & > svg {
      margin-left: 6px;
      width: 16px;
      height: 16px;
      & > path {
        stroke: ${p => p.theme.colors.text.secondary};
      }
    }
  }
`

interface Props {
  isLoading: boolean
  isOpen: boolean
  onClose: () => void
  positionInfo: PositionInfo
  supplyPoolX?: SupplyPool<string, string>
  supplyPoolY?: SupplyPool<string, string>
  pool?: ClmmPool<StructClass, unknown, string, string>
  configInfo: PositionConfigInfo<string, string, TypeArgument>
  onWithdraw: (
    reductionInfo?: ReductionInfoSubmit,
    priceX?: Price<string, string>,
    priceY?: Price<string, string>
  ) => Promise<void>
  isReversedPair: boolean
  isFrozenPosition: boolean
}

export const WITHDRAW_DIALOG_ID = 'withdraw-dialog'

const DEBOUNCE_DELAY_MS = 500

const convertToTokenList = vaultDisplayInfoList.map(v => v.vault.T)

const getInitialSliderValue = (isPositionInActive: boolean, isFrozenPosition: boolean) =>
  isPositionInActive || isFrozenPosition ? MAX_SLIDER : MIN_SLIDER

export const WithdrawDialog = ({
  isLoading,
  isOpen,
  onClose,
  positionInfo,
  supplyPoolX,
  supplyPoolY,
  pool,
  configInfo,
  onWithdraw,
  isReversedPair,
  isFrozenPosition,
}: Props) => {
  const isPositionInActive = positionInfo.lastPosition.isInactive()
  const { routerLabel } = useRouter()
  const isMobile = useIsMobile()
  const theme = useTheme()

  const { price: priceX } = useTokenPrice(configInfo.X, USDC)
  const { price: priceY } = useTokenPrice(configInfo.Y, USDC)

  // Reward fee data
  const { result: rewardsFeesInfos } = useRewardsFeesInfo(positionInfo)
  const [convertRewards, setConvertRewards] = useState(false)
  // Set initial value to SUI
  const [convertRewardsTo, setConvertRewardsTo] = useState(convertToTokenList.find(v => v.typeName === SUI.typeName)!)
  const { isLoading: isLoadingTotalValue, totalSum } = useRewardsFeesTotalValue(
    positionInfo,
    rewardsFeesInfos,
    convertRewardsTo
  )
  const { isLoading: isLoadingTotalValueUsd, totalSum: totalSumUsd } = useRewardsFeesTotalValue(
    positionInfo,
    rewardsFeesInfos,
    USDC
  )

  const pendingRewardsNum = rewardsFeesInfos
    ? Array.from(rewardsFeesInfos.rewards).length + Array.from(rewardsFeesInfos.stashed).length
    : 0

  // Amount + debounced amount
  const [amountPercentage, setAmountPercentage] = useState(() =>
    getInitialSliderValue(isPositionInActive, isFrozenPosition)
  )
  const [debouncedAmountPercentage, loadingDebouncedAmountPercentage] = useDebouncedValueWithLoading(
    amountPercentage,
    DEBOUNCE_DELAY_MS
  )

  // Withdraw details data
  const {
    slippagePct,
    handleSlippagePctChange,
    overestimationPct,
    handleOverestimationPctChange,
    afProtocols,
    handleAfProtocolUpdate,
    cetusProtocols,
    handleCetusProtocolUpdate,
    handleResetValues,
    swapType,
    handleSwapTypeChange,
  } = useWithdrawDetails()

  const { dryRunError, dryRunResult, isLoadingDryRun, resetDryRunResult } = useDryRunData({
    debouncedAmountPercentage,
    positionInfo,
    swapType,
    slippagePct,
    overestimationPct,
    afProtocols,
    cetusProtocols,
  })

  const isCalculatingDryRun = loadingDebouncedAmountPercentage || isLoadingDryRun

  const reductionInfoSubmit = useReductionInfo({
    positionInfo,
    supplyPoolX,
    supplyPoolY,
    pool,
    amountPercentage,
    slippagePct,
    overestimationPct,
    swapType,
    afProtocols,
    cetusProtocols,
    convertRewards,
    convertRewardsTo,
  })

  const handleAmountPercentageChange = useCallback(
    (newValue: number) => {
      setAmountPercentage(newValue)
      if (dryRunResult || dryRunError) {
        resetDryRunResult()
      }
    },
    [dryRunError, dryRunResult, resetDryRunResult]
  )

  const handleConvertRewardsChange = useCallback(() => {
    setConvertRewards(!convertRewards)
  }, [convertRewards])

  const handleConvertRewardsToClick = useCallback((o: CoinInfo<PhantomTypeArgument>) => {
    setConvertRewardsTo(o)
  }, [])

  const isCetusAffected = useMemo(() => {
    return CetusRedemptionUtil.configIsAffected(configInfo)
  }, [configInfo])

  if (!reductionInfoSubmit) {
    return null
  }

  const showSwap = isSwapAvailable(isCalculatingDryRun, reductionInfoSubmit, dryRunResult)

  return (
    <Dialog
      title={isPositionInActive || isFrozenPosition ? 'Close position' : 'Withdraw'}
      isOpen={isOpen}
      onClose={onClose}
      id={WITHDRAW_DIALOG_ID}
    >
      {isFrozenPosition && (
        <Banner style={{ marginTop: 24, width: 'auto' }}>
          <Typography variant={isMobile ? 'caption2' : 'body'} fontWeight="semiBold">
            CETUS compensation tokens will be claimable at{' '}
            <a href="https://app.cetus.zone/compensation" target="_blank" rel="noreferrer">
              Cetus Compensation
            </a>{' '}
            after you close your position.
          </Typography>
        </Banner>
      )}
      <AmountSlider
        isPositionInActive={isPositionInActive}
        amountPercentage={amountPercentage}
        handleAmountPercentageChange={handleAmountPercentageChange}
        isFrozenPosition={isFrozenPosition}
        isLoading={!showSwap && isLoadingDryRun}
      />

      {showSwap && (
        <>
          <PositionBreakdownTitle variant="body" color="secondary" style={{ position: 'relative' }}>
            Swap
            <Tooltip
              content="Due to asset and debt imbalance in the position, it's necessary to preform a swap to close it."
              position={isMobile ? 'bottom' : 'top'}
              tooltipBoxStyles={{ marginLeft: 150 }}
            >
              <IconInfo width={16} height={16} />
            </Tooltip>
            {isLoadingDryRun && (
              <div style={{ position: 'absolute', top: 0, left: 80 }}>
                <Spinner style={{ width: 24, height: 24 }} color={theme.colors.text.primary} />
              </div>
            )}
          </PositionBreakdownTitle>
          <ExpansionPanel
            style={{ marginBottom: 0 }}
            defaultExpanded={true}
            title={
              <ExpansionPanelTitleContainer>
                <WithdrawSummary
                  configInfo={configInfo}
                  reductionInfoSubmit={reductionInfoSubmit}
                  isReversedPair={isReversedPair}
                  pool={pool}
                  isCalculatingDryRun={isCalculatingDryRun}
                  dryRunResult={dryRunResult}
                  dryRunError={dryRunError}
                />
              </ExpansionPanelTitleContainer>
            }
          >
            <WithdrawDetails
              configInfo={configInfo}
              isReversedPair={isReversedPair}
              pool={pool}
              slippagePct={slippagePct}
              handleSlippagePctChange={handleSlippagePctChange}
              overestimationPct={overestimationPct}
              handleOverestimationPctChange={handleOverestimationPctChange}
              handleResetValues={handleResetValues}
              swapType={swapType}
              handleSwapTypeChange={handleSwapTypeChange}
              selectedProtocols={routerLabel === SwapRouter.Aftermath ? afProtocols : cetusProtocols}
              handleUpdateProtocols={
                routerLabel === SwapRouter.Aftermath ? handleAfProtocolUpdate : handleCetusProtocolUpdate
              }
            />
          </ExpansionPanel>
        </>
      )}

      <RepayDebtAndCetusRedemption
        configInfo={configInfo}
        positionInfo={positionInfo}
        amountPercentage={amountPercentage}
        isReversedPair={isReversedPair}
        isCetusAffected={isCetusAffected}
      />

      {dryRunError && <WithdrawError />}

      {isMobile && (
        <Equity
          reductionInfoSubmit={reductionInfoSubmit}
          isReversedPair={isReversedPair}
          configInfo={configInfo}
          isCalculatingDryRun={isCalculatingDryRun}
          dryRunResult={dryRunResult}
          dryRunError={dryRunError}
        />
      )}
      <div style={{ display: isMobile ? 'block' : 'flex', gap: 16 }}>
        <AdditionalContent
          $visible={Boolean(amountPercentage === MAX_SLIDER && rewardsFeesInfos && convertRewardsTo)}
          $pendingRewardsNum={pendingRewardsNum}
        >
          <PendingFeesRewardsNew
            isReversedPair={isReversedPair}
            configInfo={configInfo}
            rewards={rewardsFeesInfos}
            isWithinWithdraw
            positionInfo={positionInfo}
          />
          <CheckboxContainer onClick={handleConvertRewardsChange}>
            <Checkbox checked={convertRewards} />
            <Typography variant="body" color="secondary" style={{ marginLeft: 6 }}>
              Convert rewards to:
            </Typography>
          </CheckboxContainer>
          <ConvertRewardsToMenu
            isLoading={isLoadingTotalValue || isLoadingTotalValueUsd}
            disabled={!convertRewards}
            options={convertToTokenList}
            onOptionClick={handleConvertRewardsToClick}
            activeOption={convertRewardsTo}
            rewardsValue={totalSum}
            rewardsValueDollars={totalSumUsd}
          />
        </AdditionalContent>
        {!isMobile && (
          <div style={{ width: '50%' }}>
            <Equity
              reductionInfoSubmit={reductionInfoSubmit}
              isReversedPair={isReversedPair}
              configInfo={configInfo}
              isCalculatingDryRun={isCalculatingDryRun}
              dryRunResult={dryRunResult}
              dryRunError={dryRunError}
            />
          </div>
        )}
      </div>
      <DialogActions
        priceX={priceX}
        priceY={priceY}
        onWithdraw={onWithdraw}
        amountPercentage={amountPercentage}
        reductionInfoSubmit={reductionInfoSubmit}
        isLoading={isLoading}
      />
    </Dialog>
  )
}
